<template>
    <common-modal width="medium" @emitEvent="emitEvent($event, ['close'])" @close="emitEvent({ name: 'close' })">
        <div v-if="!showConfirmDelete" class="card-list">
            <common-page-header
                :heading="$t('pages.billing.billing.billing_list_modal.heading')"
                :content="$t('pages.billing.billing.billing_list_modal.content')"
                size="large"
                contentSpacing=""
                :isCenter="true"
            ></common-page-header>

            <common-content-spacer height="small" class="card-list">
                <common-loader v-if="isFetching" :isProcessing="isFetching"></common-loader>
                <div
                    v-for="card in cardList"
                    v-else
                    :key="card.id"
                    class="payment-card"
                    :class="{ 'payment-card--primary': card.default }"
                >
                    <div class="payment-card__content">
                        <div class="payment-card__left">
                            <div class="payment-card__brand">
                                {{ getCardBrandName(card.card.brand) }}
                            </div>

                            <div class="payment-card__number">•••• •••• •••• {{ card.card.last4 }}</div>
                        </div>

                        <div class="payment-card__right">
                            <div v-if="card.default" class="payment-card__primary">
                                <common-icon name="check-circle" size="small"></common-icon>
                                Primary
                            </div>

                            <div v-if="!card.default" v-clickaway="closeContextMenu" class="card-menu">
                                <button class="card-menu__trigger" @click.stop="toggleMenu(card)">⋮</button>

                                <transition name="fade">
                                    <div v-if="currentlyOpenMenu === card.id" class="card-menu__dropdown">
                                        <button
                                            v-for="item in menuItems(card)"
                                            :key="item.name"
                                            class="card-menu__item"
                                            @click.stop="handleMenuItemClick(item, card)"
                                        >
                                            <common-icon :name="item.icon" size="small"></common-icon>

                                            <span>{{ item.label }}</span>
                                        </button>
                                    </div>
                                </transition>
                            </div>
                        </div>
                    </div>
                </div>
            </common-content-spacer>
            <footer class="footer">
                <common-icon name="verify" size="medium"></common-icon>
                <p>{{ $t('pages.billing.billing.billing_list_modal.secure') }}</p>
            </footer>
        </div>
        <div v-else>
            <common-content-spacer height="medium">
                <div class="card-list--delete">
                    <p>{{ $t('pages.billing.billing.billing_list_modal.delete_card') }}</p>
                    <p class="card-list--delete__card">
                        <span class="card-list--item__brand">{{ getCardBrandName(deleteCard.card?.brand) }}</span>
                        **** **** **** {{ deleteCard.card?.last4 }}
                    </p>
                </div>
                <common-buttons align="center">
                    <common-button
                        :label="$t('common.microcopy.false')"
                        type="secondary"
                        @click="showConfirmDelete = false"
                    ></common-button>
                    <common-button
                        :label="$t('common.microcopy.true')"
                        type="primary"
                        @click="confirmDeleteCard()"
                    ></common-button>
                </common-buttons>
            </common-content-spacer>
        </div>
    </common-modal>
</template>

<script>
    import PracticesAPI from 'api/PracticesAPI'

    export default {
        data() {
            return {
                isFetching: false,
                showConfirmDelete: false,
                deleteCard: {},
                cardList: [],
                currentlyOpenMenu: null,
            }
        },

        methods: {
            saveDefaultCard(card) {
                PracticesAPI.setDefaultCard(card.id).then(() => {
                    this.emitEvent({ name: 'card-saved', payload: this.cardList.find((card) => card.default) })
                    this.getPracticeCards()
                })
            },

            removeItem(card) {
                this.deleteCard = card
                this.showConfirmDelete = true
            },

            confirmDeleteCard() {
                PracticesAPI.deleteCard(this.deleteCard.id).then(() => {
                    this.showConfirmDelete = false
                    this.deleteCard = {}
                    this.getPracticeCards()
                })
            },

            getPracticeCards() {
                this.isFetching = true
                PracticesAPI.getPracticeCards().then((response) => {
                    this.cardList = response
                    this.isFetching = false
                })
            },
            getCardBrandName(brand) {
                return brand.toUpperCase()
            },
            toggleMenu(card) {
                this.currentlyOpenMenu = this.currentlyOpenMenu === card.id ? null : card.id
            },

            closeContextMenu() {
                this.currentlyOpenMenu = null
            },
            menuItems(card) {
                return [
                    {
                        label: 'Set as Primary',
                        name: 'primary',
                        icon: 'check',
                        disabled: card.default,
                    },
                    {
                        label: 'Delete Card',
                        name: 'delete',
                        icon: 'trash-bin',
                        disabled: false,
                    },
                ]
            },
            handleMenuItemClick(item, card) {
                switch (item.name) {
                    case 'primary':
                        this.saveDefaultCard(card)
                        break

                    case 'delete':
                        this.removeItem(card)
                        break
                }
                this.closeContextMenu()
            },
        },

        mounted() {
            this.getPracticeCards()
        },
    }
</script>

<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    .card-list {
        margin-top: 24px;
        padding: 10px 6px 0;
        &--delete {
            p {
                text-align: center;
                margin-bottom: 20px;
            }
            &__card {
                font-size: 20px;
            }
        }
        &--item {
            &__brand {
                color: $color--txt-header;
                font-weight: 700;
                text-transform: uppercase;
            }
        }
    }
    .payment-card {
        border: 1px solid #e5e7eb;
        border-radius: 14px;
        background: #fff;
        margin-bottom: 12px;
        transition: all 0.2s ease;
        position: relative;

        &:hover {
            border-color: #c9d7f0;
            box-shadow: 0 6px 18px rgba(16, 24, 40, 0.08);
        }
        &--primary {
            background: linear-gradient(180deg, #f7fbff 0%, #edf5ff 100%);
            border: 2px solid #4d9bff;

            box-shadow: 0 8px 24px rgba(21, 112, 239, 0.12), 0 2px 6px rgba(16, 24, 40, 0.05);

            transform: translateY(-2px);
        }

        &__content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 18px 20px;
        }

        &__left {
            display: flex;
            flex-direction: column;
            gap: 8px;
        }

        &__brand {
            font-size: 18px;
            font-weight: 700;
            text-transform: capitalize;
            color: #1557c0;
        }

        &__number {
            color: #667085;
            font-size: 15px;
            letter-spacing: 1px;
        }

        &__right {
            display: flex;
            align-items: center;
            gap: 18px;
        }

        &__primary {
            display: flex;
            align-items: center;
            gap: 6px;

            background: #d9ecff;
            border: 1px solid #7db5ff;

            color: #155eef;

            padding: 8px 14px;

            border-radius: 999px;

            font-size: 13px;
            font-weight: 600;
        }
    }
    .card-menu {
        position: relative;

        &__trigger {
            cursor: pointer;
            font-size: 24px;
            font-weight: 300;
            line-height: 1;
        }

        &__dropdown {
            position: absolute;
            top: calc(100% + 8px);
            right: 0;

            width: 190px;

            background: #fff;

            border-radius: 12px;

            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
            padding: 8px;
            z-index: 100;
            overflow: hidden;
        }

        &__item {
            width: 100%;
            border-radius: 8px;
            display: flex;
            align-items: center;
            gap: 12px;

            padding: 7px 9px;
            color: #0a0a0a;
            border: none;
            background: transparent;

            text-align: left;

            cursor: pointer;

            font-size: 14px;

            transition: background 0.1s;

            &:hover {
                background: #f9fafb;
            }
        }
    }
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.15s ease;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }
    .footer {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;

        margin-top: 24px;

        color: #667085;
        font-size: 14px;

        border-top: 1px solid #eaecf0;
        padding-top: 20px;
    }
</style>
